import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, notificationAPI, paymentAPI, staffAPI, projectAPI, scheduleAPI, studioRoomAPI } from '../services/api';
import { Button, Form, Input, Modal, Select, message } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency, formatCurrencySimple } from '../utils/currency';
import StudioNavBar from '../components/common/StudioNavBar';
import { downloadStaffPaymentReceiptPdf } from '../utils/staffPaymentReceipt';
import { DEFAULT_CURRENCY_CODE } from '../constants/localeOptions';

const Dashboard: React.FC = () => {
  const { user, isStaff, canAccess } = useAuth();
  const [metrics, setMetrics] = useState<any>(null);
  const [staffPayments, setStaffPayments] = useState<any[]>([]);
  const [producerProjects, setProducerProjects] = useState<any[]>([]);
  const [producerPayments, setProducerPayments] = useState<any[]>([]);
  const [producerNotifications, setProducerNotifications] = useState<any[]>([]);
  const [ownerProjects, setOwnerProjects] = useState<any[]>([]);
  const [ownerProducers, setOwnerProducers] = useState<any[]>([]);
  const [ownerSchedules, setOwnerSchedules] = useState<any[]>([]);
  const [scheduleCalendar, setScheduleCalendar] = useState<any[]>([]);
  const [ownerStudioRooms, setOwnerStudioRooms] = useState<any[]>([]);
  const [ownerTab, setOwnerTab] = useState<'overview' | 'scheduling'>('overview');
  const [loading, setLoading] = useState(true);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<any | null>(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const navigate = useNavigate();
  const [scheduleForm] = Form.useForm();
  const selectedScheduleProjectId = Form.useWatch('project', scheduleForm);

  const getWelcomeName = () => {
    if (isStaff && user) {
      const firstName = user.first_name || '';
      const lastName = user.last_name || '';
      if (firstName || lastName) {
        return `${firstName} ${lastName}`.trim();
      }
      return user.username;
    }
    return user?.studio_name ?? user?.username;
  };

  const getWelcomeMessage = () => {
    if (user?.date_joined) {
      const joinedDate = new Date(user.date_joined);
      const now = new Date();
      // If joined within the last 24 hours, consider them new
      if (now.getTime() - joinedDate.getTime() < 24 * 60 * 60 * 1000) {
        return `Welcome, ${getWelcomeName()}`;
      }
    }
    return `Welcome back, ${getWelcomeName()}`;
  };

  const getErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    if (typeof data?.detail === 'string' && data.detail.trim()) {
      return data.detail;
    }
    if (data && typeof data === 'object') {
      const firstKey = Object.keys(data)[0];
      const firstValue = firstKey ? data[firstKey] : null;
      if (Array.isArray(firstValue) && firstValue.length > 0) {
        return String(firstValue[0]);
      }
      if (typeof firstValue === 'string') {
        return firstValue;
      }
    }
    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message;
    }
    return fallback;
  };

  const toDateTimeLocalValue = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const formatDateHeading = (dateValue: string) => {
    const date = new Date(`${dateValue}T00:00:00`);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const formatDateTime = (value: string) => {
    const date = new Date(value);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const formatTimeRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(startDate)} - ${new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(endDate)}`;
  };

  const formatRelativeDateLabel = (value: string) => {
    const targetDate = new Date(value);
    const today = new Date();
    const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate()).getTime();
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const diffDays = Math.round((targetDay - todayDay) / (24 * 60 * 60 * 1000));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === 2) return 'In two days';

    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(targetDate);
  };

  const formatCalendarEventWindow = (start: string, end: string) => {
    return `${formatRelativeDateLabel(start)} • ${formatTimeRange(start, end)}`;
  };

  const getProducerNotificationMessage = (notification: any) => {
    const titleText = String(notification?.title || '').toLowerCase();
    if (titleText.includes('cancelled')) {
      return notification.message;
    }

    const hasScheduleContext = notification?.project_name && notification?.studio_room && notification?.start_time && notification?.end_time;
    if (!hasScheduleContext) {
      return notification.message;
    }

    return `Your studio owner booked ${notification.studio_room} for ${notification.project_name} on ${formatRelativeDateLabel(notification.start_time)} from ${formatTimeRange(notification.start_time, notification.end_time)}. Please use the allocated time and space before it becomes unavailable.`;
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      if (isStaff && user) {
        const [metricsResponse, projectsResponse, paymentsResponse, notificationsResponse, staffPaymentsResponse] = await Promise.all([
          dashboardAPI.getMetrics(),
          projectAPI.getProjects(),
          paymentAPI.getPayments(),
          notificationAPI.getNotifications(),
          paymentAPI.getStaffPayments()
        ]);

        setMetrics(metricsResponse.data?.metrics || metricsResponse.data);

        const assignedProjects = (projectsResponse.data || []).filter(
          (project: any) => Number(project.assigned_producer) === Number(user.id)
        );
        const assignedProjectIds = new Set(assignedProjects.map((project: any) => Number(project.id)));
        const relatedPayments = (paymentsResponse.data || []).filter(
          (payment: any) => assignedProjectIds.has(Number(payment.project))
        );

        setProducerProjects(assignedProjects);
        setProducerPayments(relatedPayments);
        setProducerNotifications(notificationsResponse.data || []);
          setStaffPayments(staffPaymentsResponse.data || []);
        
        if (user.role === 'producer') {
          setOwnerProjects([]);
        } else {
          setOwnerProjects(projectsResponse.data || []);
        }
        
        setOwnerProducers([]);
        setOwnerSchedules([]);
        setScheduleCalendar([]);
        setOwnerStudioRooms([]);
        return;
      }

      const [metricsResponse, projectsResponse, producersResponse, schedulesResponse, calendarResponse, roomsResponse, staffPaymentsResponse] = await Promise.all([
        dashboardAPI.getMetrics(),
        projectAPI.getProjects(),
        staffAPI.getStaff(),
        scheduleAPI.getSchedules(),
        scheduleAPI.getCalendarView(),
        studioRoomAPI.getRooms(),
        paymentAPI.getStaffPayments(),
      ]);

      setMetrics(metricsResponse.data?.metrics || metricsResponse.data);
      setOwnerProjects(projectsResponse.data || []);
      setOwnerProducers(producersResponse.data || []);
      setOwnerSchedules(schedulesResponse.data || []);
      setScheduleCalendar(calendarResponse.data?.calendar || []);
      setOwnerStudioRooms(roomsResponse.data || []);
      setProducerProjects([]);
      setProducerPayments([]);
      setProducerNotifications([]);
      setStaffPayments(staffPaymentsResponse.data || []);
    } catch (error: any) {
      console.error('Failed to fetch dashboard data:', error);
      message.error(getErrorMessage(error, 'Failed to load dashboard data'));
    } finally {
      setLoading(false);
    }
  }, [isStaff, user]);

  useEffect(() => {
    if (!user && !loading) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboardData();
      }
    };

    window.addEventListener('focus', fetchDashboardData);
    document.addEventListener('visibilitychange', handleVisibilityRefresh);
    const intervalId = window.setInterval(fetchDashboardData, 30000);

    return () => {
      window.removeEventListener('focus', fetchDashboardData);
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
      window.clearInterval(intervalId);
    };
  }, [fetchDashboardData, user]);

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!user) {
    return null;
  }

  const producerCommissionRate = Number((user as any)?.commission_rate || 0);
  const paidByProject = producerPayments.reduce((acc: Record<number, number>, payment: any) => {
    const projectId = Number(payment.project);
    acc[projectId] = (acc[projectId] || 0) + Number(payment.amount || 0);
    return acc;
  }, {});
  const producerPaidSoFar = Object.values(paidByProject).reduce((sum, amount) => sum + Number(amount || 0), 0);
    const producerIncomeEarned = producerProjects.reduce((sum: number, project: any) => {
    const paidSoFar = Number(paidByProject[Number(project.id)] || 0);
    return sum + (paidSoFar * (producerCommissionRate / 100));
  }, 0);

  const pendingStaffPayments = staffPayments.filter((p: any) => p.status === 'pending');
  const approvedStaffPayments = staffPayments.filter((p: any) => p.status === 'approved');
  const totalApprovedStaffPayments = approvedStaffPayments.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);
  const producerRemainingBalance = producerIncomeEarned - totalApprovedStaffPayments;
  const staffTotalOwed = user?.role === 'producer' ? producerIncomeEarned : Number(user?.salary || 0);
  const staffCurrencyCode = user?.role === 'producer'
    ? (approvedStaffPayments[0]?.currency_code || DEFAULT_CURRENCY_CODE)
    : (user?.salary_currency || DEFAULT_CURRENCY_CODE);
  const staffRemainingBalance = Math.max(0, staffTotalOwed - totalApprovedStaffPayments);
  const recentStaffPaymentReceipts = [...staffPayments].slice(0, 6);
  const completedProducerProjects = producerProjects.filter((project: any) => project.status === 'completed' || project.status === 'delivered').length;
  const scheduleDerivedAlerts = producerProjects.flatMap((project: any) =>
    (project.scheduled_sessions || []).map((session: any) => {
      const now = Date.now();
      const startTime = new Date(session.start_time).getTime();
      const endTime = new Date(session.end_time).getTime();
      const availabilityStatus = endTime <= now ? 'expired' : startTime <= now ? 'live' : 'upcoming';
      const countdownSeconds = endTime <= now ? 0 : startTime > now ? null : Math.floor((endTime - now) / 1000);
      return {
        id: `schedule-${session.id}`,
        title: `Scheduled session for ${project.song_title}`,
        message: `Use ${session.studio_room} for ${project.song_title} within the allocated time before the slot becomes unavailable.`,
        project_name: project.song_title,
        project_custom_id: project.custom_id,
        studio_room: session.studio_room,
        start_time: session.start_time,
        end_time: session.end_time,
        countdown_seconds: countdownSeconds,
        availability_status: availabilityStatus,
        is_read: true,
        derived: true,
      };
    })
  );
  const combinedProducerNotifications = producerNotifications.length > 0 ? producerNotifications : scheduleDerivedAlerts;
  const unreadProducerNotifications = combinedProducerNotifications.filter((notification: any) => !notification.is_read).length;

  const selectedScheduleProject = ownerProjects.find((project: any) => Number(project.id) === Number(selectedScheduleProjectId));
  const scheduleEligibleProjects = ownerProjects.filter(
    (project: any) => project.assigned_producer && project.status !== 'delivered'
  );
  const upcomingSchedules = [...ownerSchedules]
    .filter((schedule: any) => new Date(schedule.end_time).getTime() >= Date.now())
    .sort((a: any, b: any) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  const todayKey = new Date().toISOString().slice(0, 10);
  const todaySchedules = ownerSchedules.filter((schedule: any) => schedule.start_time.slice(0, 10) === todayKey);
  const nextSevenDays = ownerSchedules.filter((schedule: any) => {
    const start = new Date(schedule.start_time).getTime();
    const now = Date.now();
    const sevenDays = now + (7 * 24 * 60 * 60 * 1000);
    return start >= now && start <= sevenDays;
  });
  const roomsInUse = Array.from(new Set(ownerSchedules.map((schedule: any) => (schedule.studio_room || '').trim()).filter(Boolean)));

  const openCreateScheduleModal = () => {
    setEditingSchedule(null);
    const start = new Date();
    start.setMinutes(start.getMinutes() + (60 - (start.getMinutes() % 60 || 60)));
    start.setSeconds(0, 0);
    const end = new Date(start.getTime() + (60 * 60 * 1000));
    scheduleForm.setFieldsValue({
      project: undefined,
      studio_room: ownerStudioRooms[0]?.name || undefined,
      start_time: toDateTimeLocalValue(start.toISOString()),
      end_time: toDateTimeLocalValue(end.toISOString()),
    });
    setScheduleModalOpen(true);
  };

  const openRescheduleModal = (schedule: any) => {
    setEditingSchedule(schedule);
    scheduleForm.setFieldsValue({
      project: schedule.project,
      studio_room: schedule.studio_room,
      start_time: toDateTimeLocalValue(schedule.start_time),
      end_time: toDateTimeLocalValue(schedule.end_time),
    });
    setScheduleModalOpen(true);
  };

  const closeScheduleModal = () => {
    setScheduleModalOpen(false);
    setEditingSchedule(null);
    scheduleForm.resetFields();
  };

  const handleSaveSchedule = async () => {
    try {
      const values = await scheduleForm.validateFields();
      const payload = {
        project: values.project,
        studio_room: values.studio_room,
        start_time: new Date(values.start_time).toISOString(),
        end_time: new Date(values.end_time).toISOString(),
      };

      setScheduleSaving(true);
      if (editingSchedule) {
        await scheduleAPI.rescheduleSession(editingSchedule.id, {
          start_time: payload.start_time,
          end_time: payload.end_time,
          studio_room: payload.studio_room,
        });
        message.success('Session rescheduled successfully');
      } else {
        await scheduleAPI.createSchedule(payload);
        message.success('Project session scheduled successfully');
      }

      closeScheduleModal();
      await fetchDashboardData();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(getErrorMessage(error, 'Failed to save schedule'));
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    try {
      await scheduleAPI.deleteSchedule(scheduleId);
      message.success('Scheduled session removed');
      await fetchDashboardData();
    } catch (error: any) {
      message.error(getErrorMessage(error, 'Failed to remove scheduled session'));
    }
  };

  const formatCountdown = (seconds?: number | null) => {
    if (seconds === null || seconds === undefined) return 'No timer';
    if (seconds <= 0) return 'Expired';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };

  const getNotificationStatusLabel = (notification: any) => {
    const status = notification?.availability_status;
    if (status === 'expired') return 'Expired';
    if (status === 'live') return formatCountdown(notification?.countdown_seconds);
    return 'Upcoming';
  };

  const handleMarkNotificationRead = async (id: number) => {
    try {
      await notificationAPI.markAsRead(id);
      setProducerNotifications((current) => current.map((notification: any) => (
        notification.id === id ? { ...notification, is_read: true } : notification
      )));
    } catch (error: any) {
      message.error(getErrorMessage(error, 'Failed to mark notification as read'));
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setProducerNotifications((current) => current.map((notification: any) => ({ ...notification, is_read: true })));
    } catch (error: any) {
      message.error(getErrorMessage(error, 'Failed to mark notifications as read'));
    }
  };

  const handleApproveStaffPayment = async (id: number, action: 'approved' | 'rejected') => {
    try {
      if (action === 'approved') {
        await paymentAPI.approveStaffPayment(id);
      } else {
        await paymentAPI.rejectStaffPayment(id);
      }
      message.success(`Payment ${action}`);
      await fetchDashboardData();
    } catch (error: any) {
      message.error(getErrorMessage(error, `Failed to ${action} payment`));
    }
  };


  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <StudioNavBar />

      <main className="mx-auto max-w-7xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
        <section className="rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-600 p-6 text-white shadow-xl sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-indigo-100">
                {isStaff ? 'Team workspace' : 'Studio overview'}
              </p>
              <h2 className="mt-2 text-3xl font-bold sm:text-4xl">
                {getWelcomeMessage()}
              </h2>
              <p className="mt-2 max-w-xl text-sm text-indigo-100">
                {isStaff
                  ? `You're logged into ${user?.studio_name || 'your studio'}'s workspace. View your assigned projects, monitor payments received so far, and track your earned commission.`
                  : 'Track revenue, expenses, project progress, and production sessions in one place with real-time studio visibility.'
                }
              </p>
              <div className="mt-4 inline-flex items-center gap-3 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold">
                <span className="h-2 w-2 rounded-full bg-emerald-300" />
                {isStaff ? (user?.role === 'producer' ? 'Role: Producer' : `Role: ${user?.staff_title || 'Staff'}`) : `Active subscription: ${metrics?.subscription_status ?? 'free'}`}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {canAccess('projects') && (
                <Link
                  to="/projects"
                  className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50"
                >
                  View Projects
                </Link>
              )}
              {canAccess('payments') && (
                <Link
                  to="/payments"
                  className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50"
                >
                  Record Payment
                </Link>
              )}
              {canAccess('expenses') && (
                <Link
                  to="/expenses"
                  className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50"
                >
                  Add Expense
                </Link>
              )}
            </div>
          </div>
        </section>

        {isStaff && (
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-950 px-6 py-5 text-white">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-300">Compensation Center</p>
                  <h3 className="mt-1 text-xl font-semibold">Payments, confirmations, and receipts</h3>
                  <p className="mt-1 text-sm text-slate-300">Everything your studio owner has paid you stays in one place, with confirmation actions and downloadable receipts.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-xl bg-white/10 px-4 py-3">
                    <p className="text-xs text-slate-300">Total Owed</p>
                    <p className="mt-1 font-semibold">{formatCurrencySimple(staffTotalOwed, staffCurrencyCode)}</p>
                  </div>
                  <div className="rounded-xl bg-white/10 px-4 py-3">
                    <p className="text-xs text-slate-300">Paid</p>
                    <p className="mt-1 font-semibold text-emerald-200">{formatCurrencySimple(totalApprovedStaffPayments, staffCurrencyCode)}</p>
                  </div>
                  <div className="rounded-xl bg-white/10 px-4 py-3">
                    <p className="text-xs text-slate-300">Remaining</p>
                    <p className="mt-1 font-semibold text-rose-200">{formatCurrencySimple(staffRemainingBalance, staffCurrencyCode)}</p>
                  </div>
                  <div className="rounded-xl bg-white/10 px-4 py-3">
                    <p className="text-xs text-slate-300">Pending</p>
                    <p className="mt-1 font-semibold text-amber-200">{pendingStaffPayments.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {pendingStaffPayments.length > 0 && (
              <div className="border-b border-amber-100 bg-amber-50 px-6 py-5">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h4 className="font-semibold text-amber-900">Awaiting your confirmation</h4>
                    <p className="text-sm text-amber-700">Approve received payouts to update your paid and remaining balances.</p>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                    {pendingStaffPayments.length} pending
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                  {pendingStaffPayments.map((payment: any) => (
                    <div key={payment.id} className="rounded-xl border border-amber-100 bg-white p-4 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">{formatCurrencySimple(payment.amount, payment.currency_code || staffCurrencyCode)}</p>
                          <p className="text-xs text-slate-500">{payment.receipt_number || `Receipt #${payment.id}`}</p>
                        </div>
                        <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">PENDING</span>
                      </div>
                      <p className="mt-3 text-sm text-slate-600">{payment.notes || 'No notes recorded'}</p>
                      <p className="mt-1 text-xs text-slate-500">{new Date(payment.payment_date).toLocaleDateString()}</p>
                      <div className="mt-4 flex gap-2">
                        <button onClick={() => handleApproveStaffPayment(payment.id, 'approved')} className="flex-1 rounded-lg border border-emerald-200 bg-emerald-50 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100">Approve</button>
                        <button onClick={() => handleApproveStaffPayment(payment.id, 'rejected')} className="flex-1 rounded-lg border border-rose-200 bg-rose-50 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100">Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h4 className="text-lg font-semibold text-slate-900">Receipt ledger</h4>
                <p className="text-sm text-slate-500">Approved, rejected, and pending payouts remain visible for audit and download.</p>
              </div>
            </div>
              <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {staffPayments.length > 0 ? (
                staffPayments.map((payment: any) => (
                  <div key={payment.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{formatCurrencySimple(payment.amount, payment.currency_code || staffCurrencyCode)}</p>
                        <p className="text-xs text-slate-500">{payment.receipt_number || `Receipt #${payment.id}`} - {new Date(payment.payment_date).toLocaleDateString()}</p>
                        <p className="mt-2 text-sm text-slate-600">{payment.notes || 'No notes recorded'}</p>
                        <span className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${payment.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : payment.status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                          {String(payment.status || 'pending').toUpperCase()}
                        </span>
                      </div>
                      <Button
                        icon={<FilePdfOutlined />}
                        onClick={() => downloadStaffPaymentReceiptPdf(payment, {
                          studioName: user?.studio_name,
                          recipientName: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username,
                          paidToDate: totalApprovedStaffPayments,
                          remainingBalance: staffRemainingBalance,
                        })}
                      >
                        Receipt
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full flex min-h-[140px] items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-400">
                  No staff payment receipts yet.
                </div>
              )}
              </div>
            </div>
          </section>
        )}

        {isStaff && (
          <>
          {user?.role === 'producer' ? (
            <>
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-lg">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Schedule Notifications</h3>
                  <p className="text-sm text-slate-500">Stay alert to the room and time allocated by your studio owner before the slot expires.</p>
                </div>
                {producerNotifications.length > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllNotificationsRead}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700"
                  >
                    {unreadProducerNotifications > 0 ? `Mark All Read (${unreadProducerNotifications})` : 'Mark All Read'}
                  </button>
                )}
              </div>
              <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                {combinedProducerNotifications.length > 0 ? (
                  combinedProducerNotifications.slice(0, 6).map((notification: any) => {
                    const status = notification.availability_status;
                    const statusClasses = status === 'expired'
                      ? 'bg-rose-100 text-rose-700'
                      : status === 'live'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-indigo-100 text-indigo-700';
                    const cardClasses = notification.is_read
                      ? 'rounded-2xl border border-slate-200 bg-slate-50 p-4'
                      : 'rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4';
                    return (
                      <div key={notification.id} className={cardClasses}>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-slate-900">{notification.title}</p>
                              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClasses}`}>
                                {getNotificationStatusLabel(notification)}
                              </span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600">{getProducerNotificationMessage(notification)}</p>
                            <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-500 sm:grid-cols-2">
                              <div><strong>Project:</strong> {notification.project_name || 'Scheduled session'}</div>
                              <div><strong>Room:</strong> {notification.studio_room || 'TBD'}</div>
                              <div><strong>Starts:</strong> {notification.start_time ? formatDateTime(notification.start_time) : '-'}</div>
                              <div><strong>Ends:</strong> {notification.end_time ? formatDateTime(notification.end_time) : '-'}</div>
                            </div>
                          </div>
                          {!notification.is_read && !notification.derived && (
                            <button
                              type="button"
                              onClick={() => handleMarkNotificationRead(notification.id)}
                              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700"
                            >
                              Mark Read
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-full flex min-h-[160px] items-center justify-center rounded-2xl bg-slate-50 text-sm text-slate-400">
                    No schedule notifications yet.
                  </div>
                )}
              </div>
            </section>

            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"> 
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-emerald-600" />
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">My Income Earned</p>
                <div className="mt-3 text-2xl font-semibold text-emerald-600">{formatCurrency(producerIncomeEarned)}</div>
                <p className="mt-2 text-sm text-slate-500">Commission from project payments received so far.</p>
              </div>
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-500 to-red-600" />
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Remaining Balance</p>
                <div className="mt-3 text-2xl font-semibold text-red-600">{formatCurrency(Math.max(0, producerRemainingBalance))}</div>
                <p className="mt-2 text-sm text-slate-500">Money still owed (Earned minus Approved).</p>
              </div>
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 to-indigo-600" />
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Payments Received So Far</p>
                <div className="mt-3 text-2xl font-semibold text-slate-900">{formatCurrency(producerPaidSoFar)}</div>
                <p className="mt-2 text-sm text-slate-500">Only money already paid on your assigned projects.</p>
              </div>
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 to-amber-600" />
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Commission Rate</p>
                <div className="mt-3 text-2xl font-semibold text-slate-900">{producerCommissionRate}%</div>
                <p className="mt-2 text-sm text-slate-500">Applied per assigned project to paid amounts only.</p>
              </div>
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-fuchsia-500 to-fuchsia-600" />
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Assigned Projects</p>
                <div className="mt-3 text-2xl font-semibold text-slate-900">{producerProjects.length}</div>
                <p className="mt-2 text-sm text-slate-500">Completed: {completedProducerProjects}</p>
              </div>
            </section>

            <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-lg">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">My Income Breakdown</h3>
                    <p className="text-xs uppercase tracking-wide text-slate-400">Paid so far only</p>
                  </div>
                  <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    Remaining balances are not included
                  </div>
                </div>
                <div className="mt-4">
                  {producerProjects.length > 0 ? (
                    <div className="space-y-3">
                      {producerProjects.map((project: any) => {
                        const paidSoFar = Number(paidByProject[Number(project.id)] || 0);
                        const incomeEarned = paidSoFar * (producerCommissionRate / 100);

                        return (
                          <div key={project.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="font-semibold text-slate-900">{project.song_title || 'Untitled project'}</p>
                                <p className="text-xs text-slate-500">{project.custom_id || `Project #${project.id}`}</p>
                              </div>
                              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                                {project.status || 'active'}
                              </span>
                            </div>
                            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Paid so far</p>
                                <p className="mt-1 text-base font-semibold text-slate-900">{formatCurrency(paidSoFar)}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">My income</p>
                                <p className="mt-1 text-base font-semibold text-emerald-600">{formatCurrency(incomeEarned)}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex h-48 items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-400">
                      No assigned projects with income to show yet.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-lg">
                <h3 className="text-lg font-semibold text-slate-900">My Summary</h3>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span>Total earned income</span>
                    <span className="font-semibold text-emerald-600">{formatCurrency(producerIncomeEarned)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Total paid so far</span>
                    <span className="font-semibold text-indigo-600">{formatCurrency(producerPaidSoFar)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>My commission rate</span>
                    <span className="font-semibold text-amber-600">{producerCommissionRate}%</span>
                  </div>
                </div>
                <div className="mt-5 rounded-xl bg-slate-50 p-4 text-xs text-slate-500">
                  Your dashboard shows only your own income, based on your commission rate and payments already received on assigned projects.
                </div>
              </div>
            </section></>
          ) : (
            <div className="space-y-8">
              <section className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-3">
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-emerald-600" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Current Salary Overview</p>
                  <div className="mt-3 text-2xl font-semibold text-slate-900">{formatCurrencySimple(user?.salary || 0, user?.salary_currency || 'USD')}</div>
                  <p className="mt-2 text-sm text-slate-500">Your regular compensation set by the studio owner.</p>
                </div>
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 to-indigo-600" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Base Annual Income Tracker</p>
                  <div className="mt-3 text-2xl font-semibold text-emerald-600">{formatCurrencySimple((user?.salary || 0) * 12, user?.salary_currency || 'USD')}</div>
                  <p className="mt-2 text-sm text-slate-500">Your tracked estimated total gross income for the full year.</p>
                </div>
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-blue-600" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Overall Studio Projects</p>
                  <div className="mt-3 text-2xl font-semibold text-slate-900">{ownerProjects.length}</div>
                  <p className="mt-2 text-sm text-slate-500">Currently tracked active pipeline projects within the studio.</p>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-lg">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Studio Project Statistics</h3>
                    <p className="text-sm text-slate-500">Overview of the studio's general production status</p>
                  </div>
                </div>
                <div className="mt-6 flex flex-col items-center gap-6 sm:flex-row">
                  <div className="flex w-full min-w-0 max-w-sm items-center justify-center">
                    <ResponsiveContainer width="100%" height={220}>
                      {
                        (() => {
                           const activeProjects = ownerProjects.filter((p: any) => p.status !== 'delivered');
                           const newProjects = activeProjects.filter((p: any) => p.status === 'new').length;
                           const recording = activeProjects.filter((p: any) => p.status === 'recording').length;
                           const mixing = activeProjects.filter((p: any) => p.status === 'mixing').length;
                           const mastering = activeProjects.filter((p: any) => p.status === 'mastering').length;

                           const progressData = [
                             { name: 'New', value: newProjects },
                             { name: 'Recording', value: recording },
                             { name: 'Mixing', value: mixing },
                             { name: 'Mastering', value: mastering },
                           ].filter((d) => d.value > 0);

                           if (progressData.length === 0) {
                              return <div className="flex h-full items-center justify-center text-slate-400 text-sm text-center">No active projects in the studio yet</div>;
                           }

                           const COLORS = ['#f43f5e', '#f59e0b', '#8b5cf6', '#3b82f6'];
                           return (
                              <PieChart>
                                <Pie
                                  data={progressData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={60}
                                  outerRadius={80}
                                  paddingAngle={5}
                                  dataKey="value"
                                >
                                  {progressData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}
                                  itemStyle={{ color: '#0f172a', fontWeight: 600 }}
                                />
                                <Legend verticalAlign="bottom" height={36} iconType="circle" />
                              </PieChart>
                           );
                        })()
                      }
                    </ResponsiveContainer>
                  </div>
                  <div className="w-full text-sm text-slate-600">
                     <p className="mb-5">As a staff member on salary, you assist with broader internal studio operations rather than generating direct commissions. Stay up-to-date with overall activity pipeline here.</p>
                     
                     <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
                           <p className="text-xl font-bold text-slate-900">{ownerProjects.filter((p: any) => p.status === 'delivered').length}</p>
                           <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">Delivered Studio Projects</p>
                        </div>
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
                           <p className="text-xl font-bold text-slate-900">{ownerProjects.filter((p: any) => p.status !== 'delivered').length}</p>
                           <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">Ongoing Studio Projects</p>
                        </div>
                     </div>
                  </div>
                </div>
              </section>
            </div>
          )}


          </>
        )}

        {!isStaff && (
          <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Studio Workspace</h3>
                <p className="text-sm text-slate-500">Switch between your business overview and live project scheduling.</p>
              </div>
              <div className="inline-flex w-full rounded-2xl bg-slate-100 p-1 sm:w-auto">
                {[
                  { key: 'overview', label: 'Overview' },
                  { key: 'scheduling', label: 'Project Scheduling' },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setOwnerTab(tab.key as 'overview' | 'scheduling');
                      navigate(tab.key === 'scheduling' ? '/dashboard?tab=scheduling' : '/dashboard', { replace: true });
                    }}
                    className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition sm:flex-none ${ownerTab === tab.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {!isStaff && ownerTab === 'overview' && (
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Producer & Staff Payment Receipts</h3>
                <p className="text-sm text-slate-500">Download inbuilt payout receipts generated by SOMS for your team records.</p>
              </div>
              <Link to="/management" className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700">
                Manage Payments
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {recentStaffPaymentReceipts.length > 0 ? (
                recentStaffPaymentReceipts.map((payment: any) => {
                  const recipient = ownerProducers.find((staff: any) => Number(staff.id) === Number(payment.recipient));
                  const recipientName = payment.recipient_full_name || `${recipient?.first_name || ''} ${recipient?.last_name || ''}`.trim() || payment.recipient_name || 'Team member';
                  return (
                    <div key={payment.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">{recipientName}</p>
                          <p className="text-sm font-semibold text-emerald-600">{formatCurrencySimple(payment.amount, payment.currency_code || DEFAULT_CURRENCY_CODE)}</p>
                          <p className="text-xs text-slate-500">{payment.receipt_number || `Receipt #${payment.id}`} - {new Date(payment.payment_date).toLocaleDateString()}</p>
                          <span className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${payment.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : payment.status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                            {String(payment.status || 'pending').toUpperCase()}
                          </span>
                        </div>
                        <Button
                          icon={<FilePdfOutlined />}
                          onClick={() => downloadStaffPaymentReceiptPdf(payment, {
                            studioName: user?.studio_name,
                            recipientName,
                            paidToDate: payment.paid_to_date,
                            remainingBalance: payment.remaining_balance,
                          })}
                        >
                          Receipt
                        </Button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full flex min-h-[140px] items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-400">
                  No payout receipts have been generated yet.
                </div>
              )}
            </div>
          </section>
        )}

        {!isStaff && ownerTab === 'overview' && metrics && (
          <>
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                {
                  title: 'Total Revenue',
                  value: formatCurrency(metrics.total_revenue),
                  accent: 'from-indigo-500 to-indigo-600',
                },
                {
                  title: 'Total Expenses',
                  value: formatCurrency(metrics.total_expenses),
                  accent: 'from-rose-500 to-rose-600',
                },
                {
                  title: 'Net Revenue',
                  value: formatCurrency(Math.abs(metrics.net_revenue || 0)),
                  accent: (metrics.net_revenue || 0) >= 0 ? 'from-emerald-500 to-emerald-600' : 'from-amber-500 to-amber-600',
                  sub: (metrics.net_revenue || 0) < 0 ? 'Loss' : 'Profit',
                },
                {
                  title: 'Outstanding Balance',
                  value: formatCurrency(metrics.total_outstanding),
                  accent: 'from-amber-500 to-amber-600',
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${card.accent}`} />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{card.title}</p>
                  <div className="mt-3 flex items-end justify-between">
                    <div className="text-2xl font-semibold text-slate-900">{card.value}</div>
                    {card.sub && (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                        {card.sub}
                      </span>
                    )}
                  </div>
                  <div className="mt-4 h-1 w-full rounded-full bg-slate-100">
                    <div className={`h-1 rounded-full bg-gradient-to-r ${card.accent} transition-all group-hover:w-full`} style={{ width: '60%' }} />
                  </div>
                </div>
              ))}
            </section>

            <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-lg">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">Revenue & Expenses</h3>
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Last 6 months</span>
                  </div>
                  <div className="mt-4">
                    {metrics?.revenue_expenses_data && Array.isArray(metrics.revenue_expenses_data) && metrics.revenue_expenses_data.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={metrics.revenue_expenses_data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="revenue" fill="#4F46E5" name="Revenue" />
                          <Bar dataKey="expenses" fill="#EF4444" name="Expenses" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex h-64 items-center justify-center text-sm text-slate-400">
                        No revenue/expenses data available
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-lg">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">Project Status</h3>
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Distribution</span>
                  </div>
                  <div className="mt-4">
                    {metrics?.status_counts && Array.isArray(metrics.status_counts) && metrics.status_counts.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={metrics.status_counts}
                            dataKey="count"
                            nameKey="status"
                            cx="50%"
                            cy="50%"
                            outerRadius={110}
                            label
                          >
                            {metrics.status_counts.map((_: any, index: number) => {
                              const colors = ['#4F46E5', '#F59E0B', '#EC4899', '#10B981', '#3B82F6'];
                              return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                            })}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex h-64 items-center justify-center text-sm text-slate-400">
                        No project status data available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-lg">
                  <h3 className="text-lg font-semibold text-slate-900">Attention Needed</h3>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Outstanding balances</p>
                  <div className="mt-4">
                    {metrics?.attention_projects?.length > 0 ? (
                      <ul className="divide-y divide-slate-200">
                        {metrics.attention_projects.map((project: any) => (
                          <li key={project.id} className="py-4 transition hover:bg-slate-50">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-semibold text-slate-900">{project.song_title}</p>
                              <p className="text-sm font-semibold text-rose-600">
                                {formatCurrencySimple(project.outstanding_balance)} pending
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-slate-500">No projects need attention</p>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-lg">
                  <h3 className="text-lg font-semibold text-slate-900">Quick Stats</h3>
                  <div className="mt-4 space-y-3 text-sm text-slate-600">
                    <div className="flex items-center justify-between">
                      <span>Total Commissions</span>
                      <span className="font-semibold text-emerald-600">{formatCurrency(metrics?.total_commissions)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Undelivered Songs</span>
                      <span className="font-semibold text-amber-600">{metrics?.undelivered_songs ?? 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Completed Songs</span>
                      <span className="font-semibold text-indigo-600">{metrics?.completed_songs ?? 0}</span>
                    </div>
                  </div>
                  <div className="mt-5 rounded-xl bg-slate-50 p-4 text-xs text-slate-500">
                    Keep an eye on overdue balances to maintain healthy studio cashflow.
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        {!isStaff && ownerTab === 'scheduling' && (
          <>
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                {
                  title: 'Scheduled Sessions',
                  value: ownerSchedules.length,
                  accent: 'from-indigo-500 to-indigo-600',
                  note: 'All booked project sessions',
                },
                {
                  title: 'Today',
                  value: todaySchedules.length,
                  accent: 'from-emerald-500 to-emerald-600',
                  note: 'Sessions booked for today',
                },
                {
                  title: 'Next 7 Days',
                  value: nextSevenDays.length,
                  accent: 'from-amber-500 to-amber-600',
                  note: 'Upcoming sessions this week',
                },
                {
                  title: 'Studio Rooms',
                  value: roomsInUse.length,
                  accent: 'from-fuchsia-500 to-fuchsia-600',
                  note: 'Distinct rooms already in use',
                },
              ].map((card) => (
                <div key={card.title} className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${card.accent}`} />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{card.title}</p>
                  <div className="mt-3 text-2xl font-semibold text-slate-900">{card.value}</div>
                  <p className="mt-2 text-sm text-slate-500">{card.note}</p>
                </div>
              ))}
            </section>

            <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="space-y-6 xl:col-span-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Project Scheduling</h3>
                      <p className="text-sm text-slate-500">Book rooms and producers with live conflict protection for overlapping sessions.</p>
                    </div>
                    <Button type="primary" size="large" onClick={openCreateScheduleModal} disabled={scheduleEligibleProjects.length === 0 || ownerStudioRooms.length === 0}>
                      Schedule Session
                    </Button>
                  </div>
                  {(scheduleEligibleProjects.length === 0 || ownerStudioRooms.length === 0) && (
                    <div className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-700">
                      {scheduleEligibleProjects.length === 0
                        ? 'Assign a producer to an undelivered project before creating a schedule.'
                        : 'Add at least one studio room in Management before scheduling sessions.'}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Calendar View</h3>
                      <p className="text-sm text-slate-500">Grouped by date and room for quick booking checks.</p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      {scheduleCalendar.length} day{scheduleCalendar.length === 1 ? '' : 's'} scheduled
                    </span>
                  </div>
                  <div className="mt-6 space-y-4">
                    {scheduleCalendar.length > 0 ? (
                      scheduleCalendar.map((day: any) => (
                        <div key={day.date} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-2 border-b border-slate-200 pb-3 sm:flex-row sm:items-center sm:justify-between">
                            <h4 className="text-base font-semibold text-slate-900">{formatDateHeading(day.date)}</h4>
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{day.rooms.length} room{day.rooms.length === 1 ? '' : 's'}</span>
                          </div>
                          <div className="mt-4 space-y-4">
                            {day.rooms.map((room: any) => (
                              <div key={`${day.date}-${room.studio_room}`} className="rounded-xl border border-slate-200 bg-white p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <h5 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{room.studio_room}</h5>
                                  <span className="text-xs font-semibold text-slate-400">{room.events.length} session{room.events.length === 1 ? '' : 's'}</span>
                                </div>
                                <div className="mt-4 space-y-3">
                                  {room.events.map((event: any) => (
                                    <div key={event.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                                        <div>
                                          <p className="font-semibold text-slate-900">{event.project_name}</p>
                                          <p className="text-xs text-slate-500">{event.project_custom_id || `Project #${event.project}`}</p>
                                          <p className="mt-2 text-sm text-slate-600">Producer: {event.producer_name}</p>
                                          <p className="text-sm font-semibold text-indigo-600">{formatCalendarEventWindow(event.start_time, event.end_time)}</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          <button
                                            type="button"
                                            onClick={() => openRescheduleModal(event)}
                                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700"
                                          >
                                            Reschedule
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteSchedule(event.id)}
                                            className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex min-h-[240px] items-center justify-center rounded-2xl bg-slate-50 text-sm text-slate-400">
                        No sessions scheduled yet. Create your first schedule to populate the calendar.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-slate-900">Upcoming Sessions</h3>
                  <p className="text-sm text-slate-500">The next booked sessions across your studio.</p>
                  <div className="mt-4 space-y-3">
                    {upcomingSchedules.length > 0 ? (
                      upcomingSchedules.slice(0, 6).map((schedule: any) => (
                        <div key={schedule.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="font-semibold text-slate-900">{schedule.project_name}</p>
                          <p className="text-xs text-slate-500">{schedule.project_custom_id || `Project #${schedule.project}`}</p>
                          <p className="mt-2 text-sm text-slate-600">{schedule.producer_name}</p>
                          <p className="mt-1 text-sm font-semibold text-indigo-600">{formatDateTime(schedule.start_time)}</p>
                          <p className="text-xs text-slate-500">{schedule.studio_room}</p>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                        No upcoming sessions found.
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-slate-900">Scheduling Notes</h3>
                  <div className="mt-4 space-y-3 text-sm text-slate-600">
                    <p>Only projects with an assigned producer can be booked.</p>
                    <p>Producer and room conflicts are blocked automatically before saving.</p>
                    <p>Rescheduling uses the same collision protection, so timetable changes stay reliable.</p>
                  </div>
                  <div className="mt-5 rounded-xl bg-slate-50 p-4 text-xs text-slate-500">
                    Active producers in your studio: {ownerProducers.length}. Existing rooms: {roomsInUse.length > 0 ? roomsInUse.join(', ') : 'none yet'}.
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <Modal
        title={editingSchedule ? 'Reschedule Session' : 'Schedule Project Session'}
        open={scheduleModalOpen}
        onCancel={closeScheduleModal}
        onOk={handleSaveSchedule}
        confirmLoading={scheduleSaving}
        okText={editingSchedule ? 'Save Changes' : 'Create Schedule'}
        width={640}
      >
        <Form form={scheduleForm} layout="vertical">
          <Form.Item
            name="project"
            label="Project"
            rules={[{ required: true, message: 'Please select a project' }]}
          >
            <select
              disabled={!!editingSchedule}
              className="min-h-[42px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-100"
            >
              <option value="">Select project</option>
              {scheduleEligibleProjects.map((project: any) => (
                <option key={project.id} value={project.id}>
                  {(project.custom_id ? `${project.custom_id} - ` : '') + project.song_title + ` (${project.assigned_producer_name || 'No producer'})`}
                </option>
              ))}
            </select>
          </Form.Item>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Form.Item label="Assigned Producer">
              <Input
                value={selectedScheduleProject?.assigned_producer_name || editingSchedule?.producer_name || 'Select a project with a producer'}
                disabled
              />
            </Form.Item>
            <Form.Item
              name="studio_room"
              label="Studio Room"
              rules={[{ required: true, message: 'Please select a studio room' }]}
            >
              <Select
                placeholder="Select studio room"
                options={ownerStudioRooms.map((room: any) => ({ value: room.name, label: room.name }))}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Form.Item
              name="start_time"
              label="Start Time"
              rules={[{ required: true, message: 'Please choose a start time' }]}
            >
              <Input type="datetime-local" />
            </Form.Item>
            <Form.Item
              name="end_time"
              label="End Time"
              rules={[{ required: true, message: 'Please choose an end time' }]}
            >
              <Input type="datetime-local" />
            </Form.Item>
          </div>

          {(selectedScheduleProject || editingSchedule) && (
            <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
              Session window: {scheduleForm.getFieldValue('start_time') && scheduleForm.getFieldValue('end_time') ? formatTimeRange(new Date(scheduleForm.getFieldValue('start_time')).toISOString(), new Date(scheduleForm.getFieldValue('end_time')).toISOString()) : 'Select a valid time range'}
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Dashboard;











